import {
  AtSignIcon,
  HashIcon,
  LucideIcon,
  MapPinIcon,
  PhoneIcon,
  ToggleLeftIcon,
  TypeIcon,
} from "lucide-react";

import type { FontOption } from "@/context/DesignerSettingsContext";

export const BLEED_INCHES = 0.125;
export const DESIGN_DPI = 300;
// Prevents re-render jitter from sub-pixel changes when measuring bleed metrics.
export const BLEED_METRICS_TOLERANCE_PX = 0.5;

export enum LayerTypeNames {
  TEXT = "text",
  IMAGE = "image",
  QR = "qr",
  PERSONALIZED_URL = "personalized-url",
  RECTANGLE = "rectangle-svg",
  CIRCLE = "circle-svg",
  LINE = "line-svg",
  // Vector path layer (Inspire contract: geometry lives in meta.path).
  // Deliberately NOT in IMAGE_AWARE_LAYER_TYPES — a box-level image fill
  // would paint outside the path; image-in-a-shape is image layer + meta.clip.
  PATH = "path-svg",
  POSTAGE = "postage",
  ADDRESS_BLOCK = "addressBlock",
  RETURN_ADDRESS = "returnAddress",
}

/** Layer types that can carry an image (image layer + shapes with image fill). */
export const IMAGE_AWARE_LAYER_TYPES: ReadonlySet<string> = new Set([
  LayerTypeNames.IMAGE,
  LayerTypeNames.CIRCLE,
  LayerTypeNames.RECTANGLE,
  LayerTypeNames.LINE,
]);

/** True for an image layer or a shape that can hold an image fill. */
export const isImageAwareLayer = (type: string): boolean =>
  IMAGE_AWARE_LAYER_TYPES.has(type);

export const TIME_FILTER_OPTIONS = [
  { label: "24h", addArgs: { hours: 24 } },
  { label: "7d", addArgs: { days: 7 } },
  { label: "14d", addArgs: { days: 14 } },
  { label: "30d", addArgs: { days: 30 } },
  { label: "60d", addArgs: { days: 60 } },
  { label: "1y", addArgs: { months: 12 } },
];
export const KLAVIYO_OAUTH_SCOPES =
  "flows:read flows:write lists:read lists:write metrics:read metrics:write profiles:read profiles:write campaigns:read segments:read segments:write tags:read tags:write";

export enum INTEGRATION_TYPES {
  Klaviyo = "klaviyo",
}

export enum IntegrationType {
  KlaviyoV1 = "klaviyo-v1",
}

export const MAILTECH_ORG_COOKIE = "mailtech.current-organization";
export const MAILTECH_ORG_HISTORY_COOKIE = "mailtech.recent-organizations";

export const DEFAULT_REDIRECT = "/";
export const SIGN_IN_ROUTE = "/sign-in";
export const SIGN_OUT_ROUTE = "/sign-out";

export const DASHBOARD_FILTERS_COOKIE_NAME = "mailtech-dashboard-filters";

export enum UserRole {
  Admin = "admin",
  Manager = "manager",
  User = "user",
}

export enum WebhookEventType {
  ACCOUNT_EDIT = "account.edit",
  DATAORDER_CREATE = "dataorder.create",
  DATAORDER_EDIT = "dataorder.edit",
  DATAORDER_STATUS = "dataorder.status",
}

export enum WebhookStatus {
  ACTIVE = "active",
  DISABLED = "disabled",
  DELETED = "deleted",
}

export enum WebhookEventStatus {
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
}

export enum IngestionStatus {
  PENDING = "pending",
  UPLOADING = "uploading",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum CampaignStatus {
  DRAFT = "draft",
  LIVE = "live",
  PENDING = "pending",
  ENDED = "ended",
  PAUSED = "paused",
  ERROR = "error",
}

export enum CampaignRunStatusLegacy {
  MAIL_SENT = "mail_sent",
  VALIDATED = "validated",
  UNSENT = "unsent",
  ERROR = "error",
  STARTED = "started",
}

export enum CampaignRunStatus {
  SUCCESS = "success",
  VALIDATED = "validated",
  WARNING = "warning",
  ERROR = "error",
  STARTED = "started",
}

export enum FollowUpEventType {
  ADD_TO_LIST = "add_to_list",
  REMOVE_FROM_LIST = "remove_from_list",
}

export enum TacticType {
  MASS_MAIL = "mass_mail",
  WEBHOOK = "webhook",
  NEW_SUBSCRIBER = "new_subscriber",
  // StrataAdmin-owned collection bucket for mail pieces produced outside
  // MailTech's send tactics. Never offered in the create-campaign picker.
  MANAGED = "managed",
  DO_NOT_USE_UNLESS_TESTING = "test",
}

export enum EditionStatus {
  ACTIVE = "Active",
  DRAFT = "Draft",
  INACTIVE = "Inactive",
}

export enum DesignType {
  OFFLINE = "offline",
  DESIGNER = "designer",
}

export enum DesignStatus {
  DRAFT = "draft",
  IN_REVIEW = "in-review",
  APPLIED = "applied",
  ARCHIVED = "archived",
}

export const states = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export const countries = [
  "United States",
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo (Congo-Brazzaville)",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czechia (Czech Republic)",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini (fmr. 'Swaziland')",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Holy See",
  "Honduras",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Korea (North)",
  "Korea (South)",
  "Kosovo",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar (formerly Burma)",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine State",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
];

export const KPIs = [
  "Sent",
  "Delivered",
  "Clicks",
  "Conversions",
  "CPC",
  "ROAS",
] as const;

export const SIDEBAR_COOKIE_NAME = "mailtech.sidebar";

export enum UploadDataType {
  // Asset = "ASSET",
  Data = "DATA",
}

export enum Actions {
  CREATE = "CREATE",
  READ = "READ",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  SHARE = "SHARE",
}

export enum Resources {
  USERS = "USERS",
  ACCESS_RECORDS = "ACCESS_RECORDS",
  INTEGRATIONS = "INTEGRATIONS",
  MAILERS = "MAILERS",
  CONVERSIONS = "CONVERSIONS",
  WEBHOOKS = "WEBHOOKS",
  TEST_ADDRESSES = "TEST_ADDRESSES",
  ORGANIZATIONS = "ORGANIZATIONS",
  LISTS = "LISTS",
  TEMPLATES = "TEMPLATES",
  API_KEYS = "API_KEYS",
}

export enum MAILTECH_ENVS {
  PROD = "https://app.mailtech.io",
  DEV = "https://app.dev.mailtech.io",
  STAGING = "https://app.staging.mailtech.io",
  LOCAL = "http://localhost:3000",
}

export enum CommandPaletteEvent {
  Open = "MAILTECH_OPEN_COMMAND_PALETTE",
  Opened = "MAILTECH_COMMAND_PALETTE_OPENED",
  Select = "MAILTECH_SELECT_COMMAND_PALETTE_ITEM",
  Selected = "MAILTECH_COMMAND_PALETTE_ITEM_SELECTED",
}

export enum NotificationPaneEvent {
  Open = "MAILTECH_OPEN_NOTIFICATION_PANE",
}

export enum AssistantPaneEvent {
  Open = "MAILTECH_OPEN_ASSISTANT_PANE",
  /** Push a prompt into the assistant from any surface (see askAssistant). */
  Ask = "MAILTECH_ASK_ASSISTANT",
}

/** User-confirmed app controls the assistant can trigger (see assistantActions). */
export enum AssistantLeverEvent {
  Trigger = "MAILTECH_ASSISTANT_LEVER_TRIGGER",
}

/** Proactive help: an error surfaced and the assistant can explain it. */
export enum AssistantHelpEvent {
  Offer = "MAILTECH_ASSISTANT_HELP_OFFER",
}

/** Assistant → audience-AI group builder hand-off (GroupAIPanel consumes). */
export enum AssistantAudienceEvent {
  Ask = "MAILTECH_ASSISTANT_AUDIENCE_ASK",
  Received = "MAILTECH_ASSISTANT_AUDIENCE_RECEIVED",
  /** Builder → assistant: outcome of an assistant-initiated turn, echoed
   * into the chat so clarifications can be answered there. */
  Result = "MAILTECH_ASSISTANT_AUDIENCE_RESULT",
}

export enum NotificationType {
  File = "FILE",
  List = "LIST",
}

export type NotificationKind = "upload" | "package_build";

export const DEFAULT_FILE_SLICE_SIZE = 10 * 1024 * 1024; // 10MB
export const MIN_MULTIPART_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export enum OOXMLCellType {
  Boolean = "b",
  Date = "d",
  Error = "e",
  InlineString = "inlineStr",
  Number = "n",
  String = "s",
  Formula = "str",
}

export enum DashboardFilterSchema {
  GEOGRAPHIC_DATA = "Geographic Data",
  DEMOGRAPHIC_DATA = "Demographic Data",
  ORGANIZATIONS = "Organizations",
  MAILERS = "Mailers",
  CAMPAIGNS = "Campaigns",
  LISTS = "Lists",
}

export const PAGE_SIZE_PARAM = "limit" as const;
export const PAGE_PARAM = "page" as const;
export const SORT_KEY_PARAM = "sortKey" as const;
export const SORT_DIR_PARAM = "sortDir" as const;
export const TABLE_SEARCH_PARAM = "search" as const;

export enum IngestionResourceType {
  Subscribers = "subscribers",
  Conversions = "conversions",
}

export enum AddressEntityCollectionField {
  ADDRESS_1 = "address_1",
  ADDRESS_2 = "address_2",
  CITY = "city",
  STATE = "state",
  ZIP = "zip",
  LATITUDE = "latitude",
  LONGITUDE = "longitude",
}

export const DO_NOT_MAP = "do_not_map";

export const EntityModelFieldDataTypeIcon: Record<
  MT.EntityModelFieldDataType,
  LucideIcon
> = {
  BOOLEAN: ToggleLeftIcon,
  COLLECTION: MapPinIcon,
  EMAIL: AtSignIcon,
  INT: HashIcon,
  NUMBER: HashIcon,
  PHONE_NUMBER: PhoneIcon,
  STRING: TypeIcon,
};

export const EntityModelFieldTypeLabel: Record<MT.EntityModelType, string> = {
  COMPANY: "Company",
  INDIVIDUAL: "Individual",
};

export enum RuleType {
  ASSET = "Asset",
  DEFAULT_LITERAL = "DefaultLiteral",
  ENTITY_MODEL_FIELD = "EntityModelField",
  RULE = "Rule",
}

export enum CreativeVariableDataType {
  STRING = "string",
  NUMBER = "number",
  BOOLEAN = "boolean",
  ASSET = "asset",
}

export const CreativeVariableDataTypeLabel: Record<
  CreativeVariableDataType,
  string
> = {
  [CreativeVariableDataType.STRING]: "Text",
  [CreativeVariableDataType.NUMBER]: "Number",
  [CreativeVariableDataType.BOOLEAN]: "Boolean",
  [CreativeVariableDataType.ASSET]: "Asset",
};

export enum CreativeVariableDefaultSource {
  LITERAL = "literal",
  ENTITY_MODEL_FIELD = "entity_model_field",
  ASSET = "asset",
}

export const CreativeVariableDefaultSourceLabel: Record<
  CreativeVariableDefaultSource,
  string
> = {
  [CreativeVariableDefaultSource.LITERAL]: "Literal",
  [CreativeVariableDefaultSource.ENTITY_MODEL_FIELD]: "Entity Model Field",
  [CreativeVariableDefaultSource.ASSET]: "Asset",
};

export enum FONT_SOURCES {
  SYSTEM = "System Fonts",
  GOOGLE = "Google Fonts",
  CUSTOM = "Custom Fonts",
  LIBRARY = "Library",
}

export const FONT_OPTIONS: FontOption[] = [
  {
    label: "Inter",
    value: "Inter",
    fontSource: FONT_SOURCES.SYSTEM,
  },
  {
    label: "Roboto",
    value: "Roboto",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Open Sans",
    value: "Open Sans",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Lato",
    value: "Lato",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Montserrat",
    value: "Montserrat",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Poppins",
    value: "Poppins",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Raleway",
    value: "Raleway",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Playfair Display",
    value: "Playfair Display",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Merriweather",
    value: "Merriweather",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Oswald",
    value: "Oswald",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Source Sans Pro",
    value: "Source Sans Pro",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Nunito",
    value: "Nunito",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Ubuntu",
    value: "Ubuntu",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Dancing Script",
    value: "Dancing Script",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Pacifico",
    value: "Pacifico",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Bebas Neue",
    value: "Bebas Neue",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Crimson Text",
    value: "Crimson Text",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "PT Sans",
    value: "PT Sans",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Rubik",
    value: "Rubik",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Libre Franklin",
    value: "Libre Franklin",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "DM Sans",
    value: "DM Sans",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Alegreya",
    value: "Alegreya",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Lora",
    value: "Lora",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Bitter",
    value: "Bitter",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Noto Sans",
    value: "Noto Sans",
    fontSource: FONT_SOURCES.GOOGLE,
  },
  {
    label: "Nunito Sans",
    value: "Nunito Sans",
    fontSource: FONT_SOURCES.GOOGLE,
  },
];

// ── Data Requests ───────────────────────────────────────────────────────────

export enum DataRequestStatus {
  DRAFT = "draft",
  ESTIMATING = "estimating",
  /**
   * Planned rename of `ready`; the PostgreSQL enum value doesn't exist yet.
   * Use {@link DataRequestStatus.READY} when comparing against API responses.
   * @see isDataRequestEstimatedStatus — accepts both.
   */
  ESTIMATED = "estimated",
  /** Pre-count finished; ready for `POST /fulfill`. Current canonical API value. */
  READY = "ready",
  FULFILLING = "fulfilling",
  FULFILLED = "fulfilled",
  /** CSV produced but Cosmic list ingestion failed; retry `POST /fulfill`. */
  INGESTION_FAILED = "ingestion_failed",
  FAILED = "failed",
}

export const DATA_REQUEST_STATUS_OPTIONS = [
  { label: "Draft", value: DataRequestStatus.DRAFT },
  { label: "Estimating", value: DataRequestStatus.ESTIMATING },
  { label: "Estimated", value: DataRequestStatus.READY },
  { label: "Fulfilling", value: DataRequestStatus.FULFILLING },
  { label: "Fulfilled", value: DataRequestStatus.FULFILLED },
  { label: "Ingestion Failed", value: DataRequestStatus.INGESTION_FAILED },
  { label: "Failed", value: DataRequestStatus.FAILED },
] as const;
